import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, ExternalLink, Calendar, DollarSign, Edit, Save, X, Trash2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import api from '../utils/api';
import toast from 'react-hot-toast';
import Layout from './Layout';

interface Founder {
  id: number;
  firstName: string;
  lastName: string;
  profilePictureUrl?: string;
  accountType: string;
}

interface Pitch {
  id: number;
  startupName: string;
  shortDescription: string;
  fullDescription: string;
  fundingAmount: number;
  companyName?: string;
  companyWebsite?: string;
  investorOnlyContent?: string;
  labels: string[];
  isPublic: boolean;
  isFunded: boolean;
  createdAt: string;
  updatedAt: string;
  founder: Founder;
}

interface ApiResponse {
  pitch: Pitch;
}

const PitchDetailPage: React.FC = () => {
  const { pitchId } = useParams<{ pitchId: string }>();
  const navigate = useNavigate();
  const [pitch, setPitch] = useState<Pitch | null>(null);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [editedPitch, setEditedPitch] = useState<Pitch | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { user } = useAuth();
  const { themeClasses, isLightTheme } = useTheme();

  useEffect(() => {
    if (pitchId) {
      console.log(user?.accountType);
      fetchPitch();
    }
  }, [pitchId]);

  const fetchPitch = async () => {
    try {
      setLoading(true);
      const response = await api.get<ApiResponse>(`/pitches/${pitchId}`);
      console.log(response.data.pitch);
      setPitch(response.data.pitch);
      setEditedPitch(response.data.pitch); // Initialize editedPitch
    } catch (error) {
      console.error('Error fetching pitch:', error);
      toast.error('Failed to load pitch details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditedPitch(pitch); // Reset to original data
  };

  const handleSave = async () => {
    if (!editedPitch) return;

    try {
      setIsSaving(true);
      const response = await api.put(`/pitches/${pitchId}`, {
        startupName: editedPitch.startupName,
        shortDescription: editedPitch.shortDescription,
        fullDescription: editedPitch.fullDescription,
        fundingAmount: editedPitch.fundingAmount,
        companyName: editedPitch.companyName,
        companyWebsite: editedPitch.companyWebsite,
        investorOnlyContent: editedPitch.investorOnlyContent,
        labels: editedPitch.labels,
        isPublic: editedPitch.isPublic
      });

      setPitch(response.data.pitch);
      setEditedPitch(response.data.pitch);
      setIsEditing(false);
      toast.success('Pitch updated successfully!');
    } catch (error) {
      console.error('Error updating pitch:', error);
      toast.error('Failed to update pitch');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!pitchId) return;

    try {
      setIsDeleting(true);
      await api.delete(`/pitches/${pitchId}`);
      toast.success('Pitch deleted successfully!');
      navigate('/');
    } catch (error) {
      console.error('Error deleting pitch:', error);
      toast.error('Failed to delete pitch');
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const handleFieldChange = (field: keyof Pitch, value: any) => {
    if (!editedPitch) return;
    setEditedPitch({
      ...editedPitch,
      [field]: value
    });
  };

  const isOwner = user && pitch && user.id === pitch.founder.id;

  if (loading) {
    return (
      <div className={`min-h-screen ${themeClasses.background} flex items-center justify-center`}>
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className={themeClasses.textSecondary}>Loading pitch details...</p>
        </div>
      </div>
    );
  }

  if (!pitch) {
    return (
      <div className={`min-h-screen ${themeClasses.background} flex items-center justify-center`}>
        <div className="text-center">
          <p className={themeClasses.textSecondary}>Pitch not found</p>
          <button
            onClick={() => navigate('/')}
            className={`${themeClasses.button} mt-4 px-4 py-2 rounded-lg font-medium transition-colors duration-200`}
          >
            Back to Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <Layout title="Pitch Details" showBackButton onBack={() => navigate('/')}>
      {/* Owner Actions */}
      {isOwner && (
        <div className="p-4 pb-0 flex justify-end space-x-2">
          {isEditing ? (
            <>
              <button
                onClick={handleSave}
                disabled={isSaving}
                className={`${themeClasses.button} px-3 py-1.5 rounded-lg flex items-center text-sm ${
                  isSaving ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                <Save className="w-4 h-4 mr-1" />
                {isSaving ? 'Saving...' : 'Save'}
              </button>
              <button
                onClick={handleCancel}
                disabled={isSaving}
                className={`${themeClasses.buttonSecondary} px-3 py-1.5 rounded-lg flex items-center text-sm`}
              >
                <X className="w-4 h-4 mr-1" />
                Cancel
              </button>
            </>
          ) : (
            <>
              <button
                onClick={handleEdit}
                className={`${themeClasses.button} px-3 py-1.5 rounded-lg flex items-center text-sm`}
              >
                <Edit className="w-4 h-4 mr-1" />
                Edit
              </button>
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="px-3 py-1.5 rounded-lg flex items-center text-sm text-red-400 hover:text-red-300 hover:bg-red-900/20 transition-colors"
              >
                <Trash2 className="w-4 h-4 mr-1" />
                Delete
              </button>
            </>
          )}
        </div>
      )}

      {/* Pitch Content */}
      <div className="p-4 space-y-6">
        {/* Founder Info */}
        <div className={`${themeClasses.card} rounded-lg`}>
          <div className="flex items-center mb-4">
            <div className={`w-12 h-12 ${isLightTheme ? 'bg-gray-300' : 'bg-gray-600'} rounded-full flex items-center justify-center mr-4`}>
              {pitch.founder.profilePictureUrl ? (
                <img
                  src={pitch.founder.profilePictureUrl}
                  alt={`${pitch.founder.firstName} ${pitch.founder.lastName}`}
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <User className={`w-6 h-6 ${themeClasses.textSecondary}`} />
              )}
            </div>
            <div>
              <h3 className={`text-lg font-semibold ${themeClasses.text}`}>
                {pitch.founder.firstName} {pitch.founder.lastName}
              </h3>
              <p className={`${themeClasses.textSecondary} text-sm capitalize`}>
                {pitch.founder.accountType.replace('_', ' ')}
              </p>
            </div>
          </div>
        </div>

        {/* Startup Info */}
        <div className={`${themeClasses.card} rounded-lg`}>
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              {isEditing ? (
                <div className="space-y-4">
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                      Startup Name
                    </label>
                    <input
                      type="text"
                      value={editedPitch?.startupName || ''}
                      onChange={(e) => handleFieldChange('startupName', e.target.value)}
                      className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                      placeholder="Enter startup name"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                      Short Description
                    </label>
                    <input
                      type="text"
                      value={editedPitch?.shortDescription || ''}
                      onChange={(e) => handleFieldChange('shortDescription', e.target.value)}
                      className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                      placeholder="Brief description of your startup"
                    />
                  </div>
                  <div>
                    <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                      Funding Amount
                    </label>
                    <input
                      type="number"
                      value={editedPitch?.fundingAmount || ''}
                      onChange={(e) => handleFieldChange('fundingAmount', parseFloat(e.target.value) || 0)}
                      className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                      placeholder="Enter funding amount"
                    />
                  </div>
                </div>
              ) : (
                <>
                  <h2 className={`text-2xl font-bold ${themeClasses.text} mb-2`}>{pitch.startupName}</h2>
                  <p className={`${themeClasses.textSecondary} text-lg`}>{pitch.shortDescription}</p>
                </>
              )}
            </div>
            {!isEditing && (
              <div className="text-right">
                <p className="text-2xl font-bold text-primary-400">
                  {formatCurrency(pitch.fundingAmount)}
                </p>
                <p className={`${themeClasses.textSecondary} text-sm`}>Funding Goal</p>
              </div>
            )}
          </div>

          {/* Labels */}
          <div className="flex flex-wrap gap-2 mb-4">
            {isEditing ? (
              <div className="w-full">
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Labels
                </label>
                <div className="space-y-2">
                  {['Alpha'].map((label) => (
                    <label key={label} className="flex items-center">
                      <input
                        type="checkbox"
                        checked={editedPitch?.labels.includes(label) || false}
                        onChange={(e) => {
                          const currentLabels = editedPitch?.labels || [];
                          const newLabels = e.target.checked
                            ? [...currentLabels, label]
                            : currentLabels.filter(l => l !== label);
                          handleFieldChange('labels', newLabels);
                        }}
                        className={`w-4 h-4 text-primary-600 ${isLightTheme ? 'bg-white border-gray-300' : 'bg-gray-700 border-gray-600'} rounded focus:ring-primary-500 focus:ring-2`}
                      />
                      <span className={`ml-2 ${themeClasses.textSecondary}`}>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <>
                {pitch.labels.map((label) => (
                  <span
                    key={label}
                    className={`px-3 py-1 ${isLightTheme ? 'bg-gray-200 text-gray-700' : 'bg-gray-700 text-gray-300'} text-sm rounded-full`}
                  >
                    {label}
                  </span>
                ))}
                {pitch.isFunded && (
                  <span className="px-3 py-1 bg-green-600 text-white text-sm rounded-full">
                    Funded
                  </span>
                )}
              </>
            )}
          </div>

          {/* Company Info */}
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Company Name
                </label>
                <input
                  type="text"
                  value={editedPitch?.companyName || ''}
                  onChange={(e) => handleFieldChange('companyName', e.target.value)}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="Enter company name"
                />
              </div>
              <div>
                <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                  Company Website
                </label>
                <input
                  type="url"
                  value={editedPitch?.companyWebsite || ''}
                  onChange={(e) => handleFieldChange('companyWebsite', e.target.value)}
                  className={`${themeClasses.input} w-full rounded-lg px-3 py-2`}
                  placeholder="https://example.com"
                />
              </div>
            </div>
          ) : (
            pitch.companyName && (
              <div className="mb-4">
                <h4 className={`${themeClasses.text} font-semibold mb-2`}>Company</h4>
                <p className={themeClasses.textSecondary}>{pitch.companyName}</p>
                {pitch.companyWebsite && (
                  <a
                    href={pitch.companyWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center text-primary-400 hover:text-primary-300 mt-1"
                  >
                    <ExternalLink className="w-4 h-4 mr-1" />
                    Visit Website
                  </a>
                )}
              </div>
            )
          )}
        </div>

        {/* Full Description */}
        <div className={`${themeClasses.card} rounded-lg`}>
          <h3 className={`text-xl font-semibold ${themeClasses.text} mb-4`}>About the Startup</h3>
          {isEditing ? (
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Full Description
              </label>
              <textarea
                value={editedPitch?.fullDescription || ''}
                onChange={(e) => handleFieldChange('fullDescription', e.target.value)}
                className={`${themeClasses.input} w-full h-32 resize-none rounded-lg px-3 py-2`}
                placeholder="Provide a detailed description of your startup, including your vision, mission, and what makes your company unique..."
              />
            </div>
          ) : (
            <div className="prose prose-invert max-w-none">
              <p className={`${themeClasses.textSecondary} leading-relaxed whitespace-pre-wrap`}>
                {pitch.fullDescription}
              </p>
            </div>
          )}
        </div>

        {/* Investor Only Content */}
        {/* {isEditing ? (
          <div className={`${themeClasses.card} border-l-4 border-primary-500 rounded-lg`}>
            <h3 className={`text-xl font-semibold ${themeClasses.text} mb-4`}>
              Investor Information
            </h3>
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                Investor-Only Content
              </label>
              <textarea
                value={editedPitch?.investorOnlyContent || ''}
                onChange={(e) => handleFieldChange('investorOnlyContent', e.target.value)}
                className={`${themeClasses.input} w-full h-32 resize-none rounded-lg px-3 py-2`}
                placeholder="Provide additional information that will only be visible to investors and founders..."
              />
            </div>
          </div>
        ) : (
          pitch.investorOnlyContent && (user?.accountType === 'group_a' || user?.accountType === 'owner') && (
            <div className={`${themeClasses.card} border-l-4 border-primary-500 rounded-lg`}>
              <h3 className={`text-xl font-semibold ${themeClasses.text} mb-4`}>
                Investor Information
              </h3>
              <div className="prose prose-invert max-w-none">
                <p className={`${themeClasses.textSecondary} leading-relaxed whitespace-pre-wrap`}>
                  {pitch.investorOnlyContent}
                </p>
              </div>
            </div>
          )
        )} */}

        {/* Pitch Details */}
        <div className={`${themeClasses.card} rounded-lg`}>
          <h3 className={`text-xl font-semibold ${themeClasses.text} mb-4`}>Pitch Details</h3>
          {isEditing ? (
            <div className="space-y-4">
              <div>
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={editedPitch?.isPublic || false}
                    onChange={(e) => handleFieldChange('isPublic', e.target.checked)}
                    className={`w-4 h-4 text-primary-600 ${isLightTheme ? 'bg-white border-gray-300' : 'bg-gray-700 border-gray-600'} rounded focus:ring-primary-500 focus:ring-2`}
                  />
                  <span className={`ml-2 ${themeClasses.textSecondary}`}>Make pitch public</span>
                </label>
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="flex items-center">
                <Calendar className={`w-5 h-5 ${themeClasses.textSecondary} mr-3`} />
                <div>
                  <p className={`${themeClasses.textSecondary} text-sm`}>Created</p>
                  <p className={themeClasses.text}>{formatDate(pitch.createdAt)}</p>
                </div>
              </div>
              {pitch.updatedAt !== pitch.createdAt && (
                <div className="flex items-center">
                  <Calendar className={`w-5 h-5 ${themeClasses.textSecondary} mr-3`} />
                  <div>
                    <p className={`${themeClasses.textSecondary} text-sm`}>Last Updated</p>
                    <p className={themeClasses.text}>{formatDate(pitch.updatedAt)}</p>
                  </div>
                </div>
              )}
              <div className="flex items-center">
                <DollarSign className={`w-5 h-5 ${themeClasses.textSecondary} mr-3`} />
                <div>
                  <p className={`${themeClasses.textSecondary} text-sm`}>Status</p>
                  <p className={`${pitch.isFunded ? 'text-green-400' : 'text-yellow-400'}`}>
                    {pitch.isFunded ? 'Funded' : 'Seeking Funding'}
                  </p>
                </div>
              </div>
              <div className="flex items-center">
                <User className={`w-5 h-5 ${themeClasses.textSecondary} mr-3`} />
                <div>
                  <p className={`${themeClasses.textSecondary} text-sm`}>Visibility</p>
                  <p className={`${pitch.isPublic ? 'text-green-400' : 'text-red-400'}`}>
                    {pitch.isPublic ? 'Public' : 'Private'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className={`${themeClasses.card} max-w-md w-full rounded-lg`}>
            <div className="flex items-center mb-4">
              <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mr-3">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className={`text-lg font-semibold ${themeClasses.text}`}>Delete Pitch</h3>
                <p className={`${themeClasses.textSecondary} text-sm`}>This action cannot be undone</p>
              </div>
            </div>
            
            <p className={`${themeClasses.textSecondary} mb-6`}>
              Are you sure you want to delete "{pitch?.startupName}"? This action will permanently remove the pitch and all associated data.
            </p>
            
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
                className={`${themeClasses.buttonSecondary} px-4 py-2 rounded-lg transition-colors ${
                  isDeleting ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                Cancel
              </button>
              <button
                onClick={handleDelete}
                disabled={isDeleting}
                className="flex items-center px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                {isDeleting ? 'Deleting...' : 'Delete Pitch'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default PitchDetailPage; 